import { useEffect, type FC, type ReactNode } from 'react';
import { useFragment, useRefetchableFragment, graphql } from 'react-relay';
import classnames from 'classnames';
import { VideoTile } from 'dibs-elements/exports/VideoTile.ts';

import { getBackgroundColor, type ImageBackgroundColor } from '../constants.ts';

import { type ProductVideo_viewer$key } from './__generated__/ProductVideo_viewer.graphql.ts';
import { type ProductVideo_item$key } from './__generated__/ProductVideo_item.graphql.ts';

import dibsCss from 'dibs-css';
import styles from '../styles/styles.scss';

const viewerFragment = graphql`
    fragment ProductVideo_viewer on Viewer
    @argumentDefinitions(itemId: { type: "String" })
    @refetchable(queryName: "ProductVideoRefetchQuery") {
        video: itemVideo(itemId: $itemId) {
            fileByVideoSize {
                link
            }
        }
    }
`;

const itemFragment = graphql`
    fragment ProductVideo_item on Item {
        serviceId
    }
`;

type Props = {
    viewer: ProductVideo_viewer$key | null | undefined;
    item: ProductVideo_item$key | null | undefined;
    children: ReactNode;
    isHovering: boolean;
    imageBackgroundColor?: ImageBackgroundColor;
    onPlay: () => void;
};

const ProductVideo: FC<Props> = ({
    viewer: viewerRef,
    item: itemRef,
    children,
    isHovering,
    imageBackgroundColor,
    onPlay,
}) => {
    const [data, refetch] = useRefetchableFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const video = data?.video;
    const { serviceId: itemId } = item || {};

    useEffect(() => {
        if (isHovering && !video && itemId) {
            refetch({ itemId });
        }
    }, [isHovering, itemId, refetch, video]);

    const sourceUrl = video?.fileByVideoSize?.link || '';
    const backgroundColor = getBackgroundColor(imageBackgroundColor);

    return (
        <div
            className={classnames(
                dibsCss.wFull,
                dibsCss.relative,
                styles.hideOverlay,
                backgroundColor
            )}
            data-tn="buyer-product-video"
        >
            {sourceUrl ? (
                <VideoTile
                    animate={isHovering}
                    sourceUrl={sourceUrl}
                    objectFit="contain"
                    objectPosition="center"
                    noBackground
                    onPlay={onPlay}
                />
            ) : null}
            {children}
        </div>
    );
};
export default ProductVideo;
